// ─── Warranty Certificate ─────────────────────────────────────────────────────
const WARRANTY_TERMS = [
  'Pitched (Sloped) Roofing Systems: Seven (7) Years',
  'Flat Roofing / Low-Slope Roofing Systems: Five (5) Years',
  'Deck Coating / Waterproof Deck Systems: Five (5) Years'
];

const WARRANTY_COVERS = [
  'Workmanship-related installation defects',
  'Leaks caused directly by improper installation by Contractor',
  'Failure of installed systems due to Contractor\'s labor or installation methods'
];

const WARRANTY_EXCLUSIONS = [
  'Roof maintenance or routine service work',
  'Roof tune-ups',
  'Tile replacement',
  'Roof repairs',
  'Temporary or emergency repairs',
  'Work performed on areas not originally installed by Contractor',
  'Damage caused by normal wear and tear',
  'Damage resulting from foot traffic, abuse, other trades or neglect',
  'Damage caused by third parties or trades',
  'Structural movement, settlement, or substrate failure',
  'Water displacement or staining',
  'Improper building design, drainage issues, or pre-existing conditions'
];

const WARRANTY_ACTS_OF_GOD = ['Windstorms', 'Earthquakes', 'Heavy rain or flooding', 'Fire'];

const WARRANTY_OWNER_RESPONSIBILITIES = [
  'Full payment of all related invoices',
  'Proper and regular roof maintenance',
  'Keeping roof drains, scuppers, and gutters clear',
  'Preventing unauthorized access or alterations to the roof',
  'Promptly notifying Contractor in writing of any observed issues. Notifications must be made within 7 days of observance.',
  'Allowing Contractor reasonable access to inspect and perform warranty work'
];

const WARRANTY_CLAIM_REQUIREMENTS = [
  'Property address',
  'Description of the issue',
  'Date the issue was first observed',
  'Photos'
];

const WARRANTY_LIABILITY_LIMITS = [
  'Interior damage',
  'Consequential or incidental damages',
  'Loss of use',
  'Human damages',
  'Mold or mildew',
  'Any costs exceeding the original scope of work'
];

const _toHtmlList = (items) => items.map((i) => `<li>${_esc(i)}</li>`).join('');

const _esc = (value) => {
  const s = String(value ?? '');
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
};

/**
 * Build the full warranty-certificate HTML content.
 * Pure function — accepts the saved formData object (no component state required).
 */
export const buildWarrantyCertificateContent = (formData = {}) => {
  const signatureBlock = formData.signatureImage
    ? `<img src="${formData.signatureImage}" alt="Signature" style="max-height:96px;border:1px solid #d1d5db;border-radius:6px;padding:4px;" />`
    : '<div style="height:80px;border:1px dashed #9ca3af;border-radius:6px;"></div>';

  return `
    ${createCompanyHeader()}
    <h1 class="form-title">CRAFTSMANSHIP WARRANTY</h1>

    <div class="form-section">
      <table>
        <tbody>
          <tr><td><strong>Property Owner</strong></td><td>${_esc(formData.propertyOwner)}</td></tr>
          <tr><td><strong>Property Address</strong></td><td>${_esc(formData.propertyAddress)}</td></tr>
          <tr><td><strong>Installation Date</strong></td><td>${_esc(formData.installationDate)}</td></tr>
          <tr><td><strong>Effective Date</strong></td><td>${_esc(formData.effectiveDate)}</td></tr>
          <tr><td><strong>Material Manufacturer</strong></td><td>${_esc(formData.materialManufacturer)}</td></tr>
        </tbody>
      </table>
    </div>

    <div class="form-section">
      <h2 class="form-subtitle">1. Warranty Coverage</h2>
      <p>JRT Roofing and Waterproofing Inc. ("Contractor") warrants that all roofing and waterproofing systems installed by Contractor will be free from defects in workmanship caused directly by Contractor during the applicable warranty period, subject to the terms, conditions, and exclusions outlined in this document.</p>
      <p><strong>This is a craftsmanship warranty only. Material warranties, if available, are provided separately by the product manufacturer and are subject to the manufacturer's terms and conditions.</strong></p>
    </div>

    <div class="form-section">
      <h2 class="form-subtitle">2. Craftsmanship Warranty Terms by System Type</h2>
      <p>The following warranty periods apply from the date of substantial project completion, unless otherwise stated in writing:</p>
      <ul>${_toHtmlList(WARRANTY_TERMS)}</ul>
    </div>

    <div class="form-section">
      <h2 class="form-subtitle">3. What This Warranty Covers</h2>
      <p>This craftsmanship warranty covers:</p>
      <ul>${_toHtmlList(WARRANTY_COVERS)}</ul>
      <p>If a covered defect is confirmed, Contractor will, at its sole discretion, repair or correct the affected portion of the work at no cost for labor.</p>
    </div>

    <div class="form-section">
      <h2 class="form-subtitle">4. What This Warranty Does NOT Cover</h2>
      <p>Unless expressly stated in writing, this warranty does not cover the following scopes of work:</p>
      <ul>
        ${_toHtmlList(WARRANTY_EXCLUSIONS)}
        <li>Damage caused by acts of God, including but not limited to:
          <ul>${_toHtmlList(WARRANTY_ACTS_OF_GOD)}</ul>
        </li>
      </ul>
      <p><strong>Maintenance, tune-ups, tile replacement, and repairs are not warrantied unless otherwise stated in writing and signed by Contractor.</strong></p>
    </div>

    <div class="form-section">
      <h2 class="form-subtitle">5. Owner Responsibilities</h2>
      <p>To maintain warranty eligibility, the Owner is responsible for:</p>
      <ul>${_toHtmlList(WARRANTY_OWNER_RESPONSIBILITIES)}</ul>
      <p>Failure to meet these responsibilities may void this warranty.</p>
    </div>

    <div class="form-section">
      <h2 class="form-subtitle">6. Warranty Claims Procedure</h2>
      <p>All warranty claims must be submitted in writing to:</p>
      <p><strong>JRT Roofing and Waterproofing Inc.</strong><br/>Email: info@jrtroofing.com<br/>Phone: (949) 742-2892</p>
      <p>Claims must include:</p>
      <ul>${_toHtmlList(WARRANTY_CLAIM_REQUIREMENTS)}</ul>
      <p>Contractor will inspect the reported condition before determining coverage. No warranty work will be performed without Contractor's written authorization.</p>
    </div>

    <div class="form-section">
      <h2 class="form-subtitle">7. Limitations of Liability</h2>
      <p>This craftsmanship warranty is limited strictly to the repair of defective workmanship.</p>
      <p>Contractor shall not be responsible for:</p>
      <ul>${_toHtmlList(WARRANTY_LIABILITY_LIMITS)}</ul>
      <p>This warranty is non-transferable unless approved in writing by Contractor.</p>
    </div>

    <div class="form-section">
      <h2 class="form-subtitle">8. Entire Agreement</h2>
      <p>This document represents the entire craftsmanship warranty provided by JRT Roofing and Waterproofing Inc. No verbal statements or representations modify this warranty unless made in writing and signed by an authorized representative of Contractor.</p>
    </div>

    <div class="form-section" style="margin-top:28px;">
      <p><strong>Authorized by:</strong><br/>JRT Roofing and Waterproofing Inc.</p>
      <table>
        <tbody>
          <tr>
            <td style="width:35%;"><strong>Signature</strong></td>
            <td>${signatureBlock}</td>
          </tr>
          <tr><td><strong>Printed Name &amp; Title</strong></td><td>${_esc(formData.printedNameTitle)}</td></tr>
          <tr><td><strong>Date</strong></td><td>${_esc(formData.signedDate)}</td></tr>
        </tbody>
      </table>
    </div>
  `;
};
// ─────────────────────────────────────────────────────────────────────────────

// Utility function for printing permit forms
export const printPermitForm = (formData, formTitle, formContent) => {
  const popupWidth = 1100;
  const popupHeight = 820;
  const left = Math.max(0, Math.round((window.screen.width - popupWidth) / 2));
  const top = Math.max(0, Math.round((window.screen.height - popupHeight) / 2));
  const popupFeatures = [
    `width=${popupWidth}`,
    `height=${popupHeight}`,
    `left=${left}`,
    `top=${top}`,
    'resizable=yes',
    'scrollbars=yes',
    'toolbar=no',
    'menubar=no',
    'location=no',
    'status=no'
  ].join(',');

  const printWindow = window.open('', 'permitPrintPreview', popupFeatures);
  if (!printWindow) return null;
  
  // Create the print content
  printWindow.document.write(`
    <html>
      <head>
        <title>Print ${formTitle}</title>
        <link href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css" rel="stylesheet">
        <style>
          @media print {
            body { 
              padding: 20px;
              font-size: 12pt;
            }
            .no-print { 
              display: none; 
            }
            table { 
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 1rem;
            }
            th, td { 
              border: 1px solid #e5e7eb;
              padding: 8px;
              text-align: left;
            }
            th { 
              background-color: #f3f4f6;
              font-weight: bold;
            }
            .page-break {
              page-break-before: always;
            }
            .permit-header {
              margin-bottom: 2rem;
            }
            .permit-footer {
              margin-top: 2rem;
              border-top: 1px solid #e5e7eb;
              padding-top: 1rem;
            }
            .company-header {
              display: flex;
              justify-content: space-between;
              margin-bottom: 2rem;
            }
            .company-logo {
              max-height: 80px;
              max-width: 200px;
            }
            .company-info {
              text-align: right;
            }
            .form-title {
              font-size: 24px;
              font-weight: bold;
              text-align: center;
              margin: 1rem 0;
            }
            .form-section {
              margin-bottom: 1.5rem;
            }
            .form-field {
              margin-bottom: 0.5rem;
            }
            .form-label {
              font-weight: bold;
            }
            .form-value {
              margin-left: 0.5rem;
            }
            @page {
              margin: 0.5in;
            }
          }
          
          /* Non-print styles */
          body { 
            font-family: Arial, sans-serif;
            line-height: 1.5;
            color: #333;
            max-width: 8.5in;
            margin: 0 auto;
            padding: 20px;
          }
          .company-header {
            display: flex;
            justify-content: space-between;
            margin-bottom: 2rem;
          }
          .company-logo {
            max-height: 80px;
            max-width: 200px;
          }
          .company-info {
            text-align: right;
          }
          .form-title {
            font-size: 24px;
            font-weight: bold;
            text-align: center;
            margin: 1rem 0;
          }
          table { 
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 1rem;
          }
          th, td { 
            border: 1px solid #e5e7eb;
            padding: 8px;
            text-align: left;
          }
          th { 
            background-color: #f3f4f6;
            font-weight: bold;
          }
          .form-section {
            margin-bottom: 1.5rem;
          }
          .form-field {
            margin-bottom: 0.5rem;
          }
          .form-label {
            font-weight: bold;
          }
          .form-value {
            margin-left: 0.5rem;
          }          .form-subtitle {
            font-size: 15px;
            font-weight: bold;
            margin-bottom: 6px;
          }          .form-subtitle {
            font-size: 15px;
            font-weight: bold;
            margin-bottom: 6px;
          }
          .print-button {
            position: fixed;
            top: 20px;
            right: 20px;
            padding: 8px 16px;
            background-color: #1e40af;
            color: white;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-weight: bold;
          }
          .print-button:hover {
            background-color: #1e3a8a;
          }
          @media print {
            .print-button {
              display: none;
            }
          }
        </style>
      </head>
      <body>
        <button class="print-button" onclick="window.print();">Print</button>
        ${formContent}
      </body>
    </html>
  `);
  
  printWindow.document.close();
  printWindow.focus();
  return printWindow;
};

const escapeHtml = (value) => {
  const stringValue = String(value ?? '');
  return stringValue
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const formatPermitFieldLabel = (value) => {
  if (!value) return '';
  return String(value)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const normalizePermitFieldValue = (value) => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (value instanceof Date) return value.toLocaleString();
  return String(value);
};

export const flattenPermitFormData = (value, prefix = '') => {
  const rows = [];

  if (Array.isArray(value)) {
    if (value.length === 0) {
      rows.push({ label: prefix || 'Value', value: '' });
      return rows;
    }

    const allPrimitive = value.every((item) => item === null || typeof item !== 'object');
    if (allPrimitive) {
      rows.push({
        label: prefix || 'Value',
        value: value.map((item) => normalizePermitFieldValue(item)).join(', ')
      });
      return rows;
    }

    value.forEach((item, index) => {
      const itemPrefix = prefix ? `${prefix} ${index + 1}` : `Item ${index + 1}`;
      rows.push(...flattenPermitFormData(item, itemPrefix));
    });

    return rows;
  }

  if (value && typeof value === 'object') {
    const entries = Object.entries(value);
    if (entries.length === 0) {
      rows.push({ label: prefix || 'Value', value: '' });
      return rows;
    }

    entries.forEach(([key, nestedValue]) => {
      const keyLabel = formatPermitFieldLabel(key);
      const nestedPrefix = prefix ? `${prefix} - ${keyLabel}` : keyLabel;
      rows.push(...flattenPermitFormData(nestedValue, nestedPrefix));
    });

    return rows;
  }

  rows.push({
    label: prefix || 'Value',
    value: normalizePermitFieldValue(value)
  });

  return rows;
};

export const buildPermitPreviewContent = ({ permit, formTitle }) => {
  const rows = flattenPermitFormData(permit?.formData || {});
  const createdAt = permit?.createdAt ? new Date(permit.createdAt).toLocaleString() : '';
  const updatedAt = permit?.updatedAt ? new Date(permit.updatedAt).toLocaleString() : '';

  const metadataRows = [
    formatTableRow('Form Type', escapeHtml(formTitle || permit?.formType || 'Permit Form')),
    formatTableRow('Created', escapeHtml(createdAt)),
    formatTableRow('Last Updated', escapeHtml(updatedAt))
  ].join('');

  const contentRows = rows.map((row) => {
    return formatTableRow(escapeHtml(row.label), escapeHtml(row.value));
  }).join('');

  return `
    ${createCompanyHeader()}
    <h1 class="form-title">${escapeHtml(formTitle || 'Permit Form')}</h1>

    <div class="form-section">
      <h2 class="form-subtitle">FORM DETAILS</h2>
      <table>
        <tbody>
          ${metadataRows}
        </tbody>
      </table>
    </div>

    <div class="form-section">
      <h2 class="form-subtitle">FORM CONTENT</h2>
      <table>
        <thead>
          <tr>
            <th>Field</th>
            <th>Value</th>
          </tr>
        </thead>
        <tbody>
          ${contentRows || `<tr><td colspan="2">No data available</td></tr>`}
        </tbody>
      </table>
    </div>
  `;
};

export const openPermitPreviewPopup = ({ permit, formTitle }) => {
  if (!permit) return false;

  // Use a form-type-specific content builder when available
  let formContent;
  if (permit.formType === 'warrantyForm') {
    formContent = buildWarrantyCertificateContent(permit.formData || {});
  } else {
    formContent = buildPermitPreviewContent({ permit, formTitle });
  }

  const popup = printPermitForm(permit.formData, formTitle || 'Permit Form', formContent);
  return Boolean(popup);
};

// Helper function to create a company header for the printed form
export const createCompanyHeader = () => {
  return `
    <div class="company-header">
      <div class="company-logo-container">
        <img src="/logo/JRT-logo-small.jpg" alt="JRT Roofing & Waterproofing" class="company-logo" />
      </div>
      <div class="company-info">
        <p>P.O. Box 3688</p>
        <p>San Clemente, CA 92672</p>
        <p>Phone: (949) 742-2892</p>
        <p>JRTROOFING.COM</p>
        <p>CA 1070037</p>
      </div>
    </div>
  `;
};

// Helper function to format a form field with label and value
export const formatFormField = (label, value) => {
  return `
    <div class="form-field">
      <span class="form-label">${label}:</span>
      <span class="form-value">${value || ''}</span>
    </div>
  `;
};

// Helper function to create a table row with label and value
export const formatTableRow = (label, value) => {
  return `
    <tr>
      <td class="form-label">${label}</td>
      <td class="form-value">${value || ''}</td>
    </tr>
  `;
};
